/**
 * Raised when a capture the user had granted goes away on its own — they closed
 * the tab being shared, or the microphone was unplugged.
 *
 * Kept distinct from the other `AudioSource` failures (MediaRecorder faults,
 * worklet crashes) because only this class of failure leaves a session the
 * server can still finish. Enhanced mode runs 10–40s behind the speech, so the
 * last turn or two is typically transcribed but not yet translated when capture
 * dies; closing the socket there makes the server read `client_disconnect` and
 * skip its final flush, and the translations it had already paid for are
 * delivered to nobody. Recognising this case is what lets the socket be drained
 * instead of dropped.
 */
export class CaptureLostError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CaptureLostError'
  }
}

/**
 * Whether a failure means "the capture ended" rather than "something broke".
 *
 * Checks the name as well as the prototype so a value that crossed a module
 * boundary still reports correctly.
 *
 * @param error Anything caught from an `AudioSource` error handler.
 */
export function isCaptureLostError(error: unknown): boolean {
  return (
    error instanceof CaptureLostError ||
    (error instanceof Error && error.name === 'CaptureLostError')
  )
}
